/** WebAuthn in the browser: contract JSON options in, contract JSON credentials out (docs/53 §4.1 passkeys). */
import type { auth } from '@ff/contracts';
import type { z } from 'zod';

type LoginOptions = z.infer<typeof auth.PasskeyLoginOptions>;
type RegisterOptions = z.infer<typeof auth.PasskeyRegisterOptions>;

export function passkeysSupported(): boolean {
  return typeof window !== 'undefined' && 'PublicKeyCredential' in window && !!navigator.credentials;
}

function b64(buf: ArrayBuffer | null | undefined): string {
  if (!buf) return '';
  const bytes = new Uint8Array(buf);
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function unb64(s: string): ArrayBuffer {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const bin = atob((s + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

export async function getPasskeyAssertion(o: LoginOptions) {
  const cred = (await navigator.credentials.get({
    publicKey: {
      challenge: unb64(o.challenge),
      rpId: o.rpId,
      timeout: o.timeout,
      userVerification: o.userVerification,
      allowCredentials: o.allowCredentials?.map((c) => ({ id: unb64(c.id), type: c.type, transports: c.transports as AuthenticatorTransport[] | undefined })),
    },
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error('cancelled');
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: b64(cred.rawId),
    type: 'public-key' as const,
    response: { clientDataJSON: b64(r.clientDataJSON), authenticatorData: b64(r.authenticatorData), signature: b64(r.signature), userHandle: r.userHandle ? b64(r.userHandle) : null },
    authenticatorAttachment: (cred.authenticatorAttachment as 'platform' | 'cross-platform' | null) ?? null,
    clientExtensionResults: {},
  };
}

export async function createPasskey(o: RegisterOptions) {
  const cred = (await navigator.credentials.create({
    publicKey: {
      challenge: unb64(o.challenge),
      rp: o.rp,
      user: { id: unb64(o.user.id), name: o.user.name, displayName: o.user.displayName },
      pubKeyCredParams: o.pubKeyCredParams,
      timeout: o.timeout,
      excludeCredentials: o.excludeCredentials?.map((c) => ({ id: unb64(c.id), type: c.type })),
      authenticatorSelection: o.authenticatorSelection,
      attestation: o.attestation,
    },
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error('cancelled');
  const r = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id,
    rawId: b64(cred.rawId),
    type: 'public-key' as const,
    response: { clientDataJSON: b64(r.clientDataJSON), attestationObject: b64(r.attestationObject), transports: r.getTransports?.() as ('internal' | 'hybrid')[] | undefined },
    authenticatorAttachment: (cred.authenticatorAttachment as 'platform' | 'cross-platform' | null) ?? null,
    clientExtensionResults: {},
  };
}
